"use client";

import { useState, useEffect, memo } from "react";
import { Icon, type IconName } from "@/components/ui/icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { coverForId } from "@/lib/image/covers";
import { pluralize } from "@/lib/string/pluralize";
import { initials } from "@/lib/string/initials";
import { cn } from "@/lib/utils";
import { useProjectActions } from "../../hooks/use-project-actions";
import { ProjectActions } from "./actions";
import type { ProjectCardData } from "./types";

export type { ProjectCardData } from "./types";

const STATUS_BADGE_VARIANT: Record<string, "success" | "warning"> = {
  Active: "success",
  Archived: "warning",
};

const VISIBILITY_CONFIG: Record<
  string,
  { variant: "private" | "info"; icon: IconName }
> = {
  Private: { variant: "private", icon: "IconLock" },
  Shared: { variant: "info", icon: "IconBuildings" },
};

const colorCache = new Map<string, string>();

function useDominantColor(imageUrl: string): string {
  const cachedColor = colorCache.get(imageUrl) ?? null;
  const [color, setColor] = useState<string | null>(() => cachedColor);

  useEffect(() => {
    if (cachedColor) {
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const size = 16;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;

        let bestSaturation = 0;
        let bestR = 124,
          bestG = 91,
          bestB = 245;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i],
            g = data[i + 1],
            b = data[i + 2];
          const max = Math.max(r, g, b),
            min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          const brightness = max / 255;

          if (
            saturation > bestSaturation &&
            brightness > 0.25 &&
            brightness < 0.92
          ) {
            bestSaturation = saturation;
            bestR = r;
            bestG = g;
            bestB = b;
          }
        }

        const computed = `#${bestR.toString(16).padStart(2, "0")}${bestG.toString(16).padStart(2, "0")}${bestB.toString(16).padStart(2, "0")}`;
        colorCache.set(imageUrl, computed);
        setColor(computed);
      } catch {
        // CORS or canvas error — keep null to use fallback
      }
    };
    img.src = imageUrl;
  }, [cachedColor, imageUrl]);

  return cachedColor || color || "#7c5bf5";
}

export const ProjectCard = memo(function ProjectCard({
  project,
  priority = false,
}: {
  project: ProjectCardData;
  priority?: boolean;
}) {
  const fallbackCover = coverForId(project.id);
  const coverForColor = project.coverImage ?? fallbackCover;
  const dominantColor = useDominantColor(coverForColor);
  const { select, manage, archive, isSelecting } = useProjectActions(
    project.id,
    {
      title: project.title,
      coverImage: project.coverImage,
      isSelected: project.selected,
    },
  );

  const ownerInitials = initials(project.owner);

  const selectLabel = `Select project: ${project.title}${project.selected ? " (currently selected)" : ""}${isSelecting ? " — switching" : ""}`;

  return (
    <div
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-xl border border-border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-shadow",
        isSelecting && "animate-pulse motion-reduce:animate-none",
      )}
    >
      <button
        type="button"
        onClick={select}
        disabled={isSelecting}
        aria-busy={isSelecting}
        aria-label={selectLabel}
        className="absolute aria-busy:cursor-default cursor-pointer inset-0 z-0 rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      />

      <div className="h-33 w-full relative overflow-hidden rounded-t-xl pointer-events-none">
        <Avatar className="size-full rounded-none">
          <AvatarImage
            src={project.coverImage ?? undefined}
            alt=""
            className="aspect-auto size-full object-cover"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
          />
          <AvatarFallback className="rounded-none p-0 bg-transparent">
            <img
              src={fallbackCover}
              alt=""
              className="size-full object-cover"
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
            />
          </AvatarFallback>
        </Avatar>
        {/* Status badge — always mounted, fades between states */}
        <div className="absolute top-3 left-[11px] pointer-events-none">
          <div
            className={cn(
              "transition-opacity duration-200 ease-out",
              isSelecting || project.selected ? "opacity-100" : "opacity-0",
            )}
          >
            <Badge variant="dark" size="sm" aria-hidden>
              {isSelecting ? "Selecting" : "Selected"}
            </Badge>
          </div>
          <div
            className={cn(
              "absolute inset-0 transition-opacity duration-200 ease-out",
              !project.selected && !isSelecting
                ? "opacity-0 group-hover:opacity-100"
                : "opacity-0",
            )}
          >
            <span className="inline-flex items-center rounded-md border border-white/50 bg-black/20 px-2 py-0.5 text-xs font-medium text-white whitespace-nowrap backdrop-blur-[2px]">
              Select
            </span>
          </div>
        </div>
      </div>

      <div
        className={cn(
          "flex flex-col justify-between p-4 h-[140px] rounded-b-xl bg-contrast/[0.02] overflow-hidden relative pointer-events-none",
          "before:absolute before:top-0 before:left-0 before:h-1/2 before:w-full before:bg-linear-to-b before:from-(--accent-color)/15 before:to-transparent before:z-0 before:pointer-events-none",
        )}
        style={{ "--accent-color": dominantColor } as React.CSSProperties}
      >
        <div className="flex flex-col gap-1.5 relative z-10">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 min-w-0">
              <Badge variant="muted" size="xs" className="min-w-0 shrink">
                <Avatar className="size-4">
                  <AvatarImage src={project.ownerAvatar} alt="" />
                  <AvatarFallback className="text-[6px] font-bold leading-none text-contrast/60">
                    {ownerInitials}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{project.owner}</span>
              </Badge>
              <Badge
                variant={STATUS_BADGE_VARIANT[project.status] ?? "success"}
                size="xs"
              >
                {project.status}
              </Badge>
            </div>
            <ProjectActions
              project={project}
              select={select}
              manage={manage}
              archive={archive}
              isSelecting={isSelecting}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {project.visibility &&
              (() => {
                const cfg = VISIBILITY_CONFIG[project.visibility];
                return (
                  <Badge variant={cfg.variant} size="xs">
                    <Icon name={cfg.icon} size={13} />
                    {project.visibility}
                  </Badge>
                );
              })()}
            {project.isOwner === false && (
              <Badge variant="info" size="xs">
                Shared with you
              </Badge>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1 relative z-0" aria-hidden="true">
          <span className="text-base font-medium text-heading leading-6 truncate">
            {project.title}
          </span>
          <div className="flex items-center gap-1 text-sm text-contrast/50 leading-5">
            <span className="truncate">{project.createdAt}</span>
            <span className="shrink-0">·</span>
            <span className="shrink-0">
              {project.jobCount} {pluralize(project.jobCount, "job")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

export function ProjectCardSkeleton({ index = 0 }: { index?: number }) {
  return (
    <div
      aria-hidden="true"
      role="presentation"
      className="flex flex-col overflow-hidden rounded-xl border border-border"
      style={{
        opacity: 0,
        animation: `skeleton-fade-in 0.4s ease-out ${index * 60}ms forwards`,
      }}
    >
      <div className="h-33 w-full rounded-t-xl bg-contrast/4" />

      <div className="flex h-[140px] flex-col justify-between rounded-b-xl bg-contrast/[0.02] p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <div className="flex items-center bg-contrast/3 rounded-md p-0.5 pr-1 gap-0.5">
              <div className="size-3 rounded-sm bg-contrast/5" />
              <div className="h-3 w-14 rounded-[3px] bg-contrast/5" />
            </div>
            <div className="rounded-md border border-contrast/5 px-1 py-0.5">
              <div className="h-3 w-10 rounded-[3px] bg-contrast/4" />
            </div>
          </div>
          <div className="flex items-center gap-[3px] pr-0.5">
            <div className="size-[3px] rounded-full bg-contrast/8" />
            <div className="size-[3px] rounded-full bg-contrast/8" />
            <div className="size-[3px] rounded-full bg-contrast/8" />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="h-[18px] w-[65%] rounded bg-contrast/5" />
          <div className="flex items-center gap-1.5">
            <div className="h-3.5 w-[90px] rounded-[3px] bg-contrast/4" />
            <div className="size-[3px] rounded-full bg-contrast/6 shrink-0" />
            <div className="h-3.5 w-[42px] rounded-[3px] bg-contrast/4" />
          </div>
        </div>
      </div>
    </div>
  );
}
